/**
 * Content module models — the marketing geography edited by the Content Manager.
 * id-keyed EAV collections under account.data.content, same _meta convention as
 * funeralCosts (objectsToArrays gives arrays client-side; server stores id-keyed).
 *   account.data.content.regions.<id>.{name, slug, state, order, published}
 *   account.data.content.suburbs.<id>.{name, slug, regionId, postcode, lat, lng, published}
 *
 * picaluna-v3 mirrors these into its /{state}/{region}/{service}/{suburb}/ tree
 * via GET /external/v1/accounts/:id/regions.
 */

export interface Region {
  _meta: {
    id: string;
    index: number;
    deleted?: boolean;
  };
  /** URL-safe metro slug — e.g. "sydney". Used in the v3 URL path. */
  slug: string;
  /** Display name — "Sydney". */
  name: string;
  /** Australian state code — NSW / VIC / QLD. */
  state?: string;
  /** Sort order on listings. */
  order?: number;
  /** Soft toggle. Default true. */
  published?: boolean;
  /** One-line lead for the page hero + listing cards. Plain text. */
  intro?: string;
  /** Optional search-results description (~155 chars). Blank = intro. */
  seoDescription?: string;
  /** Long-form page copy (Markdown, shared editor; conductor-file: images). */
  body?: string;
  /**
   * Promo panels (SERVICES-CMS.md §9.2) — rendered in a fixed slot on the
   * region's hub page; the link usually points at a bespoke page created for
   * this region in Pages.
   */
  promoPanels?: Array<{
    /** Stable id — the site derives panel image filenames from it. */
    id?: string;
    heading: string;
    copy?: string;
    imageFileId?: number | null;
    link?: { label: string; target: string } | null;
  }>;
  /** Uploaded hero photo file id — the page's EditorialHero image. */
  heroFileId?: number | null;
  /**
   * Set when one of this region's suburbs is edited or removed, cleared when
   * the region is published. Purely a nudge in the Website UI — suburbs ship
   * with their region, so a suburb edit is invisible on the site until the
   * region is published, and without this there is nothing telling you that.
   *
   * Conductor-only: the external API projects regions through an explicit
   * allow-list (external-region.service.ts), so this never reaches picaluna-v3.
   */
  suburbsChangedAt?: string;
  /**
   * The slug this item was last published under. Set when a per-item publish is
   * dispatched; used to take the OLD page down when the slug has since changed.
   *
   * Without it, renaming an item and publishing leaves the previous URL live:
   * the scoped merge keys on slug, so the base record under the old slug is not
   * in scope and is carried forward untouched while the new one is appended —
   * both pages build. It self-heals on the next unscoped publish, but until then
   * there is an orphan the confirm copy claims cannot exist.
   *
   * Conductor-only: every external projection is an explicit allow-list, so it
   * never reaches picaluna-v3.
   */
  publishedSlug?: string;
}

export interface Suburb {
  _meta: {
    id: string;
    index: number;
    deleted?: boolean;
  };
  /** URL-safe suburb slug — e.g. "randwick". */
  slug: string;
  /** Display name — "Randwick". */
  name: string;
  /** The owning region's _meta.id (NOT its slug) — the stable join key. */
  regionId: string;
  /** Postcode (Google Maps geocode fills this). */
  postcode?: string;
  /** Latitude (Google Maps geocode). */
  lat?: number;
  /** Longitude (Google Maps geocode). */
  lng?: number;
  /** Soft toggle. Default true. */
  published?: boolean;
  /** One-line lead for the page hero + listing cards. Plain text. */
  intro?: string;
  /** Optional search-results description (~155 chars). Blank = intro. */
  seoDescription?: string;
  /** Long-form page copy (Markdown, shared editor; conductor-file: images). */
  body?: string;
  /**
   * Promo panels (SERVICES-CMS.md §9.2) — rendered in a fixed slot on the
   * region's hub page; the link usually points at a bespoke page created for
   * this region in Pages.
   */
  promoPanels?: Array<{
    /** Stable id — the site derives panel image filenames from it. */
    id?: string;
    heading: string;
    copy?: string;
    imageFileId?: number | null;
    link?: { label: string; target: string } | null;
  }>;
  /** Uploaded hero photo file id — the page's EditorialHero image. */
  heroFileId?: number | null;
  /**
   * Slugs this suburb's page had before a rename while live: picaluna-v3 301s each of the live site's four service
   * addresses at the old slug to the funeral-services page at the new one (Suburbs tool, #253; one page per suburb, #257).
   */
  redirectFrom?: string[];
  /**
   * What the record is (#247): one ABS suburb (the default), a sub-region (stored as 'district', #260: Eastern Suburbs,
   * part of Sydney) or a place (a memorial park). Only a 'suburb' carries salCode, and the server allows one record per
   * salCode. A sub-region's suburbs are its members (website_subregion_suburbs), edited in the Suburbs tool.
   */
  kind?: 'suburb' | typeof SUBREGION_KIND | 'place';
  /** ABS SAL 2021 code of the suburb this page is about (kind 'suburb' only). */
  salCode?: string | null;
  /** A sub-region's outline colour on the suburb map, 1-8 (--app-subregion-n). */
  mapColour?: number | null;
  /**
   * The record in a sentence, e.g. "the Eastern Suburbs" ("Suburbs in the Eastern Suburbs"); the template's
   * {{suburb:where}} (#262). Blank = its name.
   */
  inSentence?: string | null;
  /**
   * Blocks inserted on this page only (#262), keyed by block id: CMS sections placed after a block of the record's
   * template (`after`: a template block id or '_end'). The template supplies the rest of the page.
   */
  blocks?: Record<string, SuburbBlock>;
  /** The FAQs the template's FAQ block shows on this page (#262): FAQ bank ids. Empty hides the block. */
  faqIds?: string[];
  /** Keep this page out of search (#262): it was the designed page's "Hide from search". */
  noindex?: boolean;
}

/** A block inserted on one suburb's page (#262): a CMS section and the template block it follows. */
export interface SuburbBlock extends CmsPageSection {
  after: string;
}

/**
 * The Suburb or Sub-region page template (#262): content.pageTemplates.tpl-suburb / tpl-subregion, edited once in the
 * Suburbs tool. Headings, links and data blocks with placeholders ({{suburb:name}}, …); no paragraphs. A removed
 * block stays as a tombstone (`_meta.deleted`) so suburbs' blocks placed after it keep their place.
 */
export interface PageTemplate {
  _meta: { id: string; index: number };
  key: 'suburb' | 'subregion';
  name: string;
  /** Title and meta description patterns. */
  title: string;
  description?: string;
  blocks: Record<string, PageTemplateBlock>;
}

export interface PageTemplateBlock extends Omit<CmsPageSection, '_meta'> {
  _meta: { id: string; index: number; deleted?: boolean };
}

/**
 * The stored kind of a sub-region record (#260). Kept as 'district' (renaming it would be a data migration plus a
 * release accepting both); the same constant is in conductor-server and picaluna-v3.
 */
export const SUBREGION_KIND = 'district' as const;

/**
 * Charity partner — flat id-keyed content item (no nested children), rendered
 * on picaluna-v3 at /charity-partners/<slug>.
 *   account.data.content.charities.<id>.{name, slug, description, url, logoFileId, order, published}
 */
export interface CharityPartner {
  _meta: {
    id: string;
    index: number;
    deleted?: boolean;
  };
  /** URL-safe slug — e.g. "gotcha4life". Used in the v3 /charity-partners/<slug> path. */
  slug: string;
  /** Display name — "Gotcha4Life". */
  name: string;
  /**
   * One-line relationship lead — "Picaluna is pleased to support their tireless
   * work in animal rescue and rehoming." Plain text, not Markdown: it renders as
   * the hero lead on the charity page AND as the blurb on the listing card, and
   * neither wants block formatting.
   */
  intro?: string;
  /** Long copy (Markdown) — what the charity does / why Picaluna supports them. */
  description?: string;
  /** Charity website URL. */
  url?: string;
  /** Uploaded logo file id (files.id); the external API resolves it to a signed URL.
   *  null clears a previously-set logo. */
  logoFileId?: number | null;
  /** Uploaded hero photo file id — the charity page's EditorialHero image.
   *  Without one every charity page shares the same stock ceremony photo. */
  heroFileId?: number | null;
  /** Sort order on listings. */
  order?: number;
  /** Soft toggle. Default true. */
  published?: boolean;
  /** The slug this item was last published under — see Region.publishedSlug. */
  publishedSlug?: string;
}

/**
 * Blog article — flat id-keyed content item, rendered on picaluna-v3 at
 * /blog/<slug>.
 *   account.data.content.blogs.<id>.{title, slug, excerpt, body, category,
 *     heroFileId, endorsedBy, publishedAt, published}
 */
export interface BlogArticle {
  _meta: {
    id: string;
    index: number;
    deleted?: boolean;
  };
  /** URL-safe slug — e.g. "what-is-a-vigil". Used in the v3 /blog/<slug> path. */
  slug: string;
  /** Headline. */
  title: string;
  /** ~155-char SEO summary + card blurb. */
  excerpt?: string;
  /** The article body, Markdown. */
  body?: string;
  /** Topic — Grief / Planning / Costs / Ceremony / Cremation / Burial / News. */
  category?: string;
  /** Uploaded hero image file id; the external API resolves it to a signed URL.
   *  null clears a previously-set hero. */
  heroFileId?: number | null;
  /** Endorsing arrangers — a CSV of user ids ("12,45"). Stored as a scalar so it
   *  round-trips cleanly through the content module; each links to the arranger's
   *  /funeral-directors profile on picaluna-v3. */
  endorsedBy?: string;
  /** Original/publish date (ISO) — preserved from the source for SEO + ordering. */
  publishedAt?: string;
  /** Draft/publish toggle. New posts start unpublished. */
  published?: boolean;
  /** The slug this item was last published under — see Region.publishedSlug. */
  publishedSlug?: string;
}

/**
 * CMS page (PAGE-CMS.md) — a formerly-YAML-composed picaluna.com page moved
 * into Conductor. Stored at account.data.content.pages.<id> with an id-keyed,
 * _meta.index-ordered `sections` map. A published page whose `path` matches a
 * YAML route OVERRIDES the YAML at build time.
 */
export interface CmsPageSection {
  _meta: {
    id: string;
    index: number;
  };
  type: 'editorial-hero' | 'section';
  /** Small label above the hero h1 (editorial-hero only). */
  eyebrow?: string;
  /** Hero h1 (may carry <em>…</em>) or the section h2. */
  heading?: string;
  /**
   * The block heading's level (#259): 'h3' or 'h4'. Blank = h2, which is how
   * every block rendered before the setting existed, so nothing is stored for
   * the default. picaluna-v3 sizes the heading by its level (h2 40/42, h3
   * 22/23, h4 19/21) and falls back to h2 for a value it does not know.
   */
  headingLevel?: string | null;
  /**
   * Alignment of the block's heading: 'center' or 'right'. Blank = left, how
   * every block rendered before the setting existed. The copy keeps its own
   * ::: alignment markers. (Until 2026-10-29 it aligned the buttons too.)
   */
  align?: string | null;
  /** The heading's own colour (block text palette). Blank = the Text colour. */
  headingColour?: string | null;
  /**
   * The links/buttons row: 'left' | 'center' | 'right'. Blank = the block's
   * `align` when set (it moved the buttons when it shipped), else centre.
   */
  buttonAlign?: string | null;
  /** false = no line under the block. Blank = the line. */
  divider?: boolean | null;
  /** The block's own padding: 'top' | 'bottom' | 'none'. Blank = both. */
  spacing?: string | null;
  /** Funeral Costs: the cost names to show. Blank = all. */
  costs?: string[] | null;
  /** Markdown (shared editor). Hero copy renders as a plain lead paragraph. */
  copy?: string;
  /** Static site-asset path ("services/candles.jpg") — seeded pages. */
  image?: string;
  /** Uploaded section image; wins over `image` when both are set. */
  heroFileId?: number | null;
  imageAlt?: string;
  /**
   * Where the image sits relative to the heading and copy (#239):
   * above-heading | below-heading | left | right | bottom.
   * Blank = bottom, which is where every stored section already renders it.
   */
  imageLayout?: string;
  /** Anchor id for the rendered <section>. */
  anchor?: string;
  /** SectionRenderer component key ('faq-hub', 'blog-list', …). */
  component?: string;
  variant?: string;
  /** youtube: watch URL, share link, embed URL or bare id. Stored as pasted;
   *  picaluna-v3 reduces it to the id and refuses what it cannot read. */
  video?: string;
  /** youtube: caption under the frame, also the iframe's accessible name. */
  caption?: string;
  limit?: number | null;
  perPage?: number | null;
  shuffle?: boolean;
  photosOnly?: boolean;
  /** directors-grid: cards shown at first; the rest behind "Show more". */
  showMax?: number | null;
  /** panels/gallery/blog-list: desktop columns (2–4) / row cap. Blank =
   *  adaptive / show all. directors-grid and venues-directory accept up to 6
   *  (#231, #232). */
  columns?: number | null;
  rows?: number | null;
  /**
   * Full-width background colour (#226). ADJACENT sections sharing a value
   * render inside ONE wrapper on the site, so a run of blocks reads as a
   * single coloured band and a gradient stretches across the whole run
   * instead of repeating per block. Blank = the page background.
   *
   * Values must match picaluna-v3's zod enum EXACTLY — an unknown stored
   * value aborts the whole site build, not just the page carrying it.
   */
  background?: string | null;
  /** Body text colour (#227), same palette as `background`. Blank = default. */
  textColour?: string | null;
  /**
   * Links/buttons under the block (#230). Supersedes the singular `link`,
   * which stays readable so stored sections keep rendering — the site
   * prefers `links` and falls back, so no migration was needed to ship.
   *
   * `style` is the site's CtaButton variant: 'link' (plain underlined text,
   * the pre-#230 behaviour), 'primary', 'secondary', 'tertiary'. Unbounded
   * by decision (Ivan, 2026-09-30) — a cap is easy to add later and awkward
   * to remove once authors rely on it.
   */
  links?: Array<{ label: string; target: string; style?: string }>;
  /** gallery block: uploaded images, display order. */
  images?: Array<{ fileId: number; alt?: string; caption?: string }>;
  /** downloads block: uploaded documents, display order. */
  files?: Array<{ fileId: number; label?: string; fileName?: string }>;
  buttons?: Array<{ label: string; target: string }>;
  link?: { label: string; target: string } | null;
  /**
   * Panel grid (#264): Background and Text colour for every panel in the
   * block, same palette as `background` / `textColour`. A panel's own choice
   * wins. Blank stores nothing, so the plain card stays the default.
   */
  panelBackground?: string | null;
  panelTextColour?: string | null;
  panels?: CmsPagePanel[];
  services?: string[];
  /** services record id for the service-hero/-inclusions/-faqs blocks. */
  service?: string;
  /** Scope for the regions/suburbs/venues listing blocks (blank = derived
   *  from the page's own address). */
  state?: string;
  region?: string;
  suburb?: string;
  faqs?: Array<{ question: string; answer: string }>;
  /**
   * faq-overview: ids from the account FAQ bank, chosen on THIS block (#202).
   * Empty/absent keeps the original behaviour — the bank filtered by the
   * page's own address — so pages set up before the picker keep working.
   */
  faqIds?: string[];
  /**
   * faq-overview: render a heading per category rather than one flat list
   * (#202). Categories are the free-text values on the FAQ records.
   */
  groupByCategory?: boolean;
  /**
   * A page template's block only (#262): the suburb record field it reads ('body' on a text block, 'faqIds' on the
   * FAQ block). The site hides the block when that field is empty.
   */
  bind?: string;
}

/** One card of a Panel grid block. Heading-gated: everything else is optional. */
export interface CmsPagePanel {
  heading: string;
  copy?: string;
  imageFileId?: number | null;
  imageAlt?: string;
  link?: { label: string; target: string } | null;
  /** This panel's own colours (#264); blank = the block's Panel colours. */
  background?: string | null;
  textColour?: string | null;
}

export interface CmsPage {
  _meta: {
    id: string;
    index: number;
    deleted?: boolean;
  };
  /** Site URL, e.g. "/faq". The identity picaluna-v3 joins on. */
  path: string;
  /** <title> tag / page name in the list. */
  title: string;
  /** Meta description. */
  description?: string;
  /** Pre-CMS sitemap page — the path is the SEO baseline and can't be edited. */
  locked?: boolean;
  noindex?: boolean;
  published?: boolean;
  /**
   * Addresses this page used to live at. Set automatically when the address
   * changes on a page that has been published, so the old URL 301s to the new
   * one instead of dying (CLAUDE.md §21.7 — never break a URL).
   */
  redirectFrom?: string[];
  /** id-keyed sections map (ordered by _meta.index). */
  sections?: Record<string, CmsPageSection>;
  /** The path-slug this page was last published under — see Region.publishedSlug. */
  publishedSlug?: string;
}

/**
 * Navigation menu group (PAGE-CMS.md "Menus") — one record per header group
 * or footer column, at account.data.content.menus.<id>. Order between groups
 * is _meta.index; items carry their own id-keyed, _meta.index-ordered map.
 */
export interface CmsMenuItem {
  _meta: { id: string; index: number };
  label: string;
  /** Empty when this row is a section HEADING — its children carry the links. */
  target?: string;
  /** Sub-links: the row renders as a headed section inside the dropdown. */
  children?: Record<string, { _meta: { id: string; index: number }; label: string; target: string }>;
}
export interface CmsMenu {
  _meta: { id: string; index: number; deleted?: boolean };
  name: string;
  location: 'header' | 'footer';
  /**
   * Append the LIVE per-state region list (built from published regions) —
   * how the Locations dropdown stays in step with the regions data instead of
   * being hand-maintained.
   */
  showRegions?: boolean;
  items?: Record<string, CmsMenuItem>;
}

/**
 * Template-copy fragment (PAGE-CMS.md §6) — a recurring sentence on the
 * programmatic region/suburb pages, at account.data.content.templateCopy.<id>.
 * `key` joins to picaluna-v3's template-copy registry; {placeholders} in
 * `copy` are substituted per page.
 */
export interface CmsTemplateCopy {
  _meta: { id: string; index: number; deleted?: boolean };
  /** Registry key (kebab-case) — created by the seed, not editable. */
  key: string;
  name: string;
  /** Where this sentence appears + which {placeholders} it may use. */
  description?: string;
  copy: string;
  /** Optional uploaded image (the help-band photo) — the copy doubles as
   *  the image's description for screen readers. */
  imageFileId?: number | null;
}

/**
 * Website service data record (SERVICES-CMS.md §3) — the slim structured
 * fields behind picaluna-v3's services collection, at
 * account.data.content.siteServices.<id>. The id is an immutable join key
 * ({price:<id>} tokens, service-* blocks, reference("services")); the page
 * presentation lives in CMS pages, not here.
 */
export interface CmsSiteService {
  _meta: {
    id: string;
    index: number;
  };
  name: string;
  category: 'cremation' | 'burial' | 'memorial' | 'prepaid' | 'livestream';
  shortDescription?: string;
  fullDescription?: string;
  startingPrice?: number | null;
  order?: number;
  featured?: boolean;
  published?: boolean;
  inclusions?: string[];
  faqs?: Array<{ question: string; answer: string }>;
}
